import { assert } from '@ember/debug';
import Component from '@glimmer/component';
import type { FormData, FormKey } from 'ember-headless-form';
import type { HeadlessFormComponentSignature } from 'ember-headless-form/components/headless-form';
import { type CheckboxControlSignature } from '../../../../components/form/controls/checkbox.gts';
import ChoiceField, {
  type ChoiceFieldSig
} from '../../../../components/form/fields/choice.gts';
import { type UserData } from '../../../types.ts';

export interface WrFormCheckboxFieldComponentSignature<
  DATA extends UserData,
  KEY extends FormKey<FormData<DATA>> = FormKey<FormData<DATA>>
> {
  Element: CheckboxControlSignature['Element'];
  Args: {
    label?: ChoiceFieldSig['Args']['label'];
    variant?: ChoiceFieldSig['Args']['variant'];
    onChange?: CheckboxControlSignature['Args']['onChange'];
    name: KEY;
    form: HeadlessFormComponentSignature<DATA, unknown>['Blocks']['default'][0];
  };
}

export default class WrFormCheckboxFieldComponent<
  DATA extends UserData,
  KEY extends FormKey<FormData<DATA>> = FormKey<FormData<DATA>>
> extends Component<WrFormCheckboxFieldComponentSignature<DATA, KEY>> {
  assertBoolean = (value: unknown): boolean | undefined => {
    assert(
      `Only boolean values are expected for ${String(
        this.args.name
      )}, but you passed ${typeof value}`,
      typeof value === 'undefined' || typeof value === 'boolean'
    );

    return value;
  };

  <template>
    <@form.Field @name={{@name}} as |field|>
      <ChoiceField
        @variant={{@variant}}
        @label={{@label}}
        @isChecked={{this.assertBoolean field.value}}
        as |f|
      >
        <f.Checkbox
          name={{@name}}
          @isChecked={{this.assertBoolean field.value}}
          {{! @glint-expect-error "onChange" of the checkbox only expects a boolean typed value, but field.setValue is generic, accepting anything that DATA[KEY] could be. Similar case with "@isChecked", but there casting to a boolean is easy.}}
          @onChange={{field.setValue}}
          ...attributes
        />
      </ChoiceField>
    </@form.Field>
  </template>
}
